import WorkflowDashboard from '../components/WorkflowDashboard';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProfile, fetchDeclarations, fetchRenewals } from '../services/api';

export default function DashboardPage() {
  const [profile, setProfile] = useState<any>(null);
  const [declarations, setDeclarations] = useState<any[]>([]);
  const [renewals, setRenewals] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [fptSummary,setFptSummary]=useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([
      fetchProfile().then(p => {
        setProfile(p);
        return p;
      }),
      fetchRenewals().then(setRenewals),
    ]).then(async results => {
      const profileResult = results[0];
      if (profileResult.status === 'rejected') setError(profileResult.reason?.message || 'Erreur de chargement');
      const prof = profileResult.status === 'fulfilled' ? profileResult.value : null;
      try {
        const decs = await fetchDeclarations(prof?.id);
        setDeclarations(Array.isArray(decs) ? decs : []);
      } catch (e: any) {
        console.warn('Failed to fetch declarations', e);
      }
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="loading">Chargement...</div>;
  if (error) return <div className="error-msg">{error}</div>;

  // Compute KPIs from live data
  const currentDec = declarations[0];
  const paidCount = fptSummary?.portfolio?.paid_count ?? 0;
  const totalCount = fptSummary?.portfolio?.declarations ?? 0;
  const conformityPct = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;
  const fptDue = fptSummary?.portfolio?.remaining_usd;
  const deadlines = renewals?.items || renewals?.upcoming || [];
  const validLicenses = deadlines.filter((d: any) => d.days_remaining != null && d.days_remaining > 0).length;
  const renewSoon = deadlines.filter((d: any) => d.days_remaining != null && d.days_remaining <= 90 && d.days_remaining > 0).length;
  const expired = deadlines.filter((d: any) => d.days_remaining != null && d.days_remaining <= 0).length;
  const renewalTotal = deadlines.reduce((s: number, d: any) => s + (d.amount_usd || 0), 0);

  return (
    <>
      <div className="hero">
        <div>
          <span className="badge green">&#9679; Compte actif</span>
          <h2>FPT et licences de vos établissements</h2>
        </div>
        <div>
          <strong>{profile?.name || profile?.hotelName || profile?.owner_name || 'Mon compte'}</strong><br />
          <small>{profile?.total_rooms || 0} chambres enregistrées</small>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi"><small>Conformité FPT</small><strong>{fptSummary?.portfolio?.review_count>0?'À vérifier':totalCount > 0 ? `${conformityPct} %` : '–'}</strong><em>{fptSummary?.portfolio?.review_count>0?'Rapprochement requis':fptSummary?`${paidCount}/${totalCount} payées`:'Données en cours de chargement'}</em></div>
        <div className="kpi"><small>FPT dû</small><strong>{fptSummary?.portfolio?.review_count>0?'À vérifier':fptDue!=null?`${Number(fptDue).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`:'–'}</strong><em>Toutes périodes · solde confirmé</em></div>
        <div className="kpi"><small>Licences valides</small><strong>{deadlines.length > 0 || renewals != null ? validLicenses : '–'}</strong><em>En ordre</em></div>
        <div className="kpi"><small>À renouveler ≤ 90 j</small><strong>{deadlines.length > 0 || renewals != null ? renewSoon : '–'}</strong><em style={{ color: 'var(--orange)' }}>Action requise</em></div>
        <div className="kpi"><small>Licence expirée</small><strong>{deadlines.length > 0 || renewals != null ? expired : '–'}</strong><em style={{ color: 'var(--red)' }}>Urgent</em></div>
        <div className="kpi"><small>Renouvellements prévus</small><strong>{renewalTotal > 0 ? `${renewalTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD` : (deadlines.length > 0 ? '0,00 USD' : '–')}</strong><em>12 prochains mois</em></div>
      </div>

      <WorkflowDashboard onData={setFptSummary}/>
      <div className="layout">
        <div className="card">
          <div className="head">
            <div>
              <h3>Dossier FPT en cours</h3>
              <div className="sub">{currentDec ? `${currentDec.period || ''} — ${currentDec.hotel_name || profile?.name || 'Mon établissement'}` : 'Aucune déclaration en cours'}</div>
              {currentDec && (
                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--muted)' }}>
                  Chiffre d'affaires : <strong>{Number(currentDec.collected_usd ?? currentDec.system_amount ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</strong>
                  {Number(currentDec.collected_cdf || 0) > 0 && (
                    <span> + <strong>{Number(currentDec.collected_cdf).toLocaleString('fr-FR')} CDF</strong></span>
                  )}
                  {Number(currentDec.collected_cdf || 0) > 0 && currentDec.exchange_rate && (
                    <span style={{ fontSize: 11, marginLeft: 6 }}>(Taux : 1 USD = {Number(currentDec.exchange_rate).toLocaleString('fr-FR')} CDF)</span>
                  )}
                </div>
              )}
            </div>
            {currentDec && <span className="badge">{currentDec.status||'À déclarer'}</span>}
          </div>
          <div className="actions">
            <button className="btn primary" onClick={() => navigate('/dossier')}>Poursuivre le dossier</button>
            <button className="btn" onClick={() => navigate('/declarations')}>Voir déclarations</button>
          </div>
        </div>

        <div className="card">
          <h3>Prochaines échéances</h3>
          <div className="timeline">
            {deadlines.length === 0 && <div className="sub" style={{ paddingLeft: 34 }}>Aucune échéance à venir</div>}
            {deadlines.slice(0, 5).map((d: any, i: number) => (
              <div className="event" key={i}>
                <time>{d.date || d.renewal_date || 'À définir'}</time>
                <strong>{d.establishment_name || d.name}</strong>
                <small>{d.category || ''} {d.amount_usd ? `· ${d.amount_usd} USD` : ''}</small>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
